import { useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import { fosApi, fosError, newRequestId, timeZoneChoices } from '../../lib/fosApi';
import { useFos } from './FosShell';
import FosDashboard from './FosDashboard';
import { ErrorNote, Icon, Section } from './ui';

/** The council's own time zone: it defines the council-local day for the daily financial-access acknowledgment. Never guessed on the server. */
function TimeZoneField({ value, onChange, id }: { value: string; onChange: (v: string) => void; id: string }) {
  const choices = timeZoneChoices();
  return (
    <label className="fos-field" htmlFor={id}>
      <span>Council time zone</span>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {!value && <option value="">Choose a time zone…</option>}
        {choices.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
      </select>
      <small className="fos-hint">Used to decide what "today" means for your council when people acknowledge that their access to financial information is logged.</small>
    </label>
  );
}

function Setup({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [timeZone, setTimeZone] = useState(() => timeZoneChoices()[0] ?? '');
  const turnOn = async () => {
    setBusy(true); setError('');
    try {
      const current = await fosApi.getConfig({});
      await fosApi.saveConfig({ requestId: newRequestId(), expectedVersion: current.config?.version ?? 0, patch: { enabled: true, ...(timeZone ? { timeZone } : {}) }, reason: 'Turned on from Friends of Scouting setup' });
      onDone();
    } catch (cause) { setError(fosError(cause).message); } finally { setBusy(false); }
  };
  return (
    <Section title="Set up Friends of Scouting" icon="heart">
      <p className="fos-lede">Friends of Scouting is off for this council, and no donor can be seen by anyone. Turning it on opens the Donors area to anyone whose published Permissions grant donor access, and to anyone you assign to a donor relationship whose role the policy gives access (see Permissions). Review that first if you are unsure.</p>
      <TimeZoneField id="fos-setup-tz" value={timeZone} onChange={setTimeZone} />
      <ErrorNote message={error} />
      <button className="btn primary" onClick={turnOn} disabled={busy || !timeZone}>{busy ? <Spinner /> : 'Turn on Friends of Scouting'}</button>
    </Section>
  );
}

/** Shown to those who may change settings when FOS is on but the council has not chosen a time zone yet. */
function TimeZoneSetup({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [timeZone, setTimeZone] = useState(() => timeZoneChoices()[0] ?? '');
  const save = async () => {
    setBusy(true); setError('');
    try {
      const current = await fosApi.getConfig({});
      await fosApi.saveConfig({ requestId: newRequestId(), expectedVersion: current.config?.version ?? 0, patch: { timeZone }, reason: 'Set the council time zone' });
      onDone();
    } catch (cause) { setError(fosError(cause).message); } finally { setBusy(false); }
  };
  return (
    <Section title="Choose your council's time zone" icon="clock">
      <p className="fos-lede">Financial information is not available until your council has a time zone, so that each person acknowledges access once per council day.</p>
      <TimeZoneField id="fos-tz" value={timeZone} onChange={setTimeZone} />
      <ErrorNote message={error} />
      <button className="btn primary" onClick={save} disabled={busy || !timeZone}>{busy ? <Spinner /> : 'Save time zone'}</button>
    </Section>
  );
}

export default function FosHome() {
  const { context, refresh } = useFos();
  if (!context.enabled) {
    return context.canAdministerSettings
      ? <Setup onDone={refresh} />
      : <Section title="Friends of Scouting" icon="heart"><p className="fos-lede">Friends of Scouting is not turned on for your council yet. Someone who manages Friends of Scouting settings for your council can enable it.</p></Section>;
  }
  const v = context.scopes.viewDonors;
  const reach = v.council ? 'every donor relationship in the council'
    : [v.districtIds.length ? `donors in ${v.districtIds.map((id) => context.districts.find((d) => d.id === id)?.name ?? id).join(', ')}` : '', v.relationshipCount ? `${v.relationshipCount} donor relationship${v.relationshipCount === 1 ? '' : 's'} assigned to you` : ''].filter(Boolean).join(' and ');
  return (
    <>
      {context.canAdministerSettings && !context.timeZone && <TimeZoneSetup onDone={refresh} />}
      {context.capabilities.viewProgress ? <FosDashboard /> : (
        <Section title="Your access" icon="lock">
          <p className="fos-lede">{reach ? <>You can work with <strong>{reach}</strong>.</> : 'You do not have access to donor relationships yet.'}</p>
          <div className="fos-actions">
            <Link className="btn primary" to="/fos/donors">Open the donor directory</Link>
            {context.canCreateDonors && <Link className="btn" to="/fos/donors?new=1"><Icon name="plus" size={14} /> Add a donor</Link>}
          </div>
        </Section>
      )}
    </>
  );
}
